import { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";

//MUI
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Container from "@mui/material/Container";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import LoadingButton from "@mui/lab/LoadingButton";
import useAuth from "hooks/useAuth";
import { useNavigate } from "react-router-dom";
import usePageTitle from "hooks/usePageTitle";
import { FormHelperText, Paper } from "@mui/material";
import PageTitle from "components/typography/PageTitle";

export default function ChangePassword() {
  usePageTitle("Change Password");

  const [toggleShowPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const navigate = useNavigate();
  const { changePassword } = useAuth();

  //toggle show password
  const handleClickShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  //initial values
  const initialValues = { oldPassword: "", newPassword: "" };

  //validation schema
  const validationSchema = Yup.object().shape({
    oldPassword: Yup.string().min(6).required("Enter old password"),
    newPassword: Yup.string().min(6).required("Enter new password"),
  });

  //onSubmit
  const onSubmit = async ({ oldPassword, newPassword }) => {
    try {
      setLoading(true);

      await changePassword(oldPassword, newPassword);
      navigate("/admin/dashboard");
    } catch (error) {
      setLoading(false);
      setError(error.message);
    }
  };

  //Formik hook
  const { errors, values, touched, handleBlur, handleChange, handleSubmit, isValid } = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
  });

  return (
    <Container sx={{ py: 2 }} component={Paper} maxWidth="md">
      <PageTitle title="Change Password" />

      <form noValidate onSubmit={handleSubmit}>
        <TextField
          fullWidth
          margin="normal"
          type="password"
          label="Old password"
          name="oldPassword"
          value={values.oldPassword || ""}
          onChange={handleChange}
          onBlur={handleBlur}
          error={Boolean(touched.oldPassword && errors.oldPassword)}
          helperText={touched.oldPassword && errors.oldPassword}
        />
        <TextField
          fullWidth
          margin="normal"
          name="newPassword"
          label="New Password"
          type={toggleShowPassword ? "text" : "password"}
          value={values.newPassword || ""}
          onChange={handleChange}
          onBlur={handleBlur}
          error={Boolean(touched.newPassword && errors.newPassword)}
          helperText={touched.newPassword && errors.newPassword}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  {!toggleShowPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Box sx={{ mt: 3, mb: 2 }}>
          {loading ? (
            <LoadingButton loading fullWidth variant="contained">
              Change Password
            </LoadingButton>
          ) : (
            <Button fullWidth disabled={!isValid} type="submit" variant="contained">
              Change Password
            </Button>
          )}
          {error && (
            <FormHelperText error sx={{ fontSize: "1rem" }}>
              {error}
            </FormHelperText>
          )}
        </Box>
      </form>
    </Container>
  );
}
